import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import DJPalAudioPreview from './DJPalAudioPreview';
import DJPalScrollableList from './DJPalScrollableList';
import ArtistsListModal from './ArtistsListModal';
import CardSearch from './CardSearch';
import ArtistLinksRow from './ArtistLinksRow';
import { canPlayMix, useMixPlayback, delayedClick } from './MixPlayer';
import { setArtistQuery, setActiveSearch, setTrackQuery, setMixQuery } from '../../redux/slices/djpalSlice';
import { searchArtist, searchTrack, searchMixes, ensureToken } from '../../redux/thunk/djpalThunk';
import { openMixpediaLabel, loadMoreLabelTracks, openMixpediaArtistsGrid } from '../../redux/thunk/appNavThunk';
import ErrorBanner from '../ErrorBanner';
import LikeButton from '../LikeButton';
import TrackContextMenu from '../TrackContextMenu';
import MixLikeButton from '../MixLikeButton';
import MixContextMenu from '../MixContextMenu';
import useRowContextMenu from '../../hooks/useRowContextMenu';
import MultiArtistText from '../MultiArtistText';
import CoverArt from '../CoverArt';
import SupportedCheck from '../common/SupportedCheck';
import { ReactComponent as PlayIcon } from '../../assets/icons/play.svg';
import { ReactComponent as PauseIcon } from '../../assets/icons/pause.svg';
import { resolveMixCover, useNativeMixCoverMode } from '../../utils/nativeMixCover';
import { isWeb as _isWeb, notifyWebOnly } from '../../utils/webOnly';
import { entityId } from '../../utils/entityId';
import { safeArtwork } from '../../utils/no1001';
import { track as trackEvent } from '../../utils/analytics';
import './LabelSearch.scss';
import { useDesignV2 } from '../../utils/designV2';
import HeroHeader from '../v2/HeroHeader';
import HeroPlayButton from '../v2/HeroPlayButton';
import FollowLabelButton from '../FollowLabelButton';
import NotifyBellButton from '../NotifyBellButton';
import { checkFollowedLabels } from '../../redux/thunk/followedLabelsThunk';
import CoPlayedTracks from './CoPlayedTracks';
import PageTabs, { SectionHeader } from '../v2/PageTabs';
import ListToolbar from '../v2/ListToolbar';
import AutoLoad, { SortChips } from '../v2/AutoLoad';
import { fmtNum } from '../v2/format';
import PlayAllButton from '../common/PlayAllButton';
import { useTierGate } from '../../hooks/useTierGate';
import { showUpgradeModal } from '../../redux/slices/tierSlice';
import { GoatBadge } from '../Tier';

// Same card-header glyphs the artist page uses (list view / open full page).
const ListIcon = () => (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 3 21 3 21 9"/><polyline points="9 21 3 21 3 15"/><line x1="21" y1="3" x2="14" y2="10"/><line x1="3" y1="21" x2="10" y2="14"/></svg>
);
const OpenIcon = () => (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
);

const fmt = (n) => Number(n || 0).toLocaleString();

// DESIGN V2 — the label page's tabs, like the artist page: Overview is the
// summary (a few rows of each list, "See all" opens its tab), every other tab
// is ONE full-width list paged from /labels/:id/:list.
const V2_TABS = [
    { key: 'overview', label: 'Overview' },
    { key: 'artists', label: 'Artists' },
    { key: 'mixes', label: 'Mixes' },
    { key: 'tracks', label: 'Tracks' },
];
const V2_OVERVIEW_ROWS = 6;
const LIST_PAGE_SIZE = 50;
const FREE_RESULTS_LIMIT = 5;
const ARTIST_SORTS = ['plays', 'name'];
const ARTIST_SORT_LABELS = { plays: 'Most tracks', name: 'A–Z' };
const MIX_SORTS = ['newest', 'labelTracks'];
const MIX_SORT_LABELS = { newest: 'Newest', labelTracks: 'Most label tracks' };
const TRACK_SORTS = ['played', 'newest'];
const TRACK_SORT_LABELS = { played: 'Most played', newest: 'Newest' };

// The artist page's card skeleton (title bar + four track rows), verbatim.
const CardSkeleton = () => (
    <div className="djpal-card">
        <div className="djpal-skel-block skel-card-title" />
        {[0, 1, 2, 3].map((r) => (
            <div className="djpal-skel-track-row" key={r}>
                <div className="djpal-skel-block skel-art" />
                <div className="skel-track-lines">
                    <div className="djpal-skel-block skel-line" />
                    <div className="djpal-skel-block skel-line short" />
                </div>
            </div>
        ))}
    </div>
);

// Label page ('label' view), laid out like the artist page: header (artwork,
// name, country, parent / sub-label chips) → "Supported by …" line with the
// socials icon row → six stat tiles → four cards in two rows (label tracks
// played by DJs | artists supporting the label; artists on label | mixes
// with label tracks). Data lives in djpal.labelResults (openMixpediaLabel /
// loadMoreLabelTracks in appNavThunk) so Back/Forward restore it from nav
// snapshots. `stats` / `supportingArtists` / `artistsOnLabel` / `mixes` are
// optional (older servers omit them) — the tiles / cards hide when absent.
const LabelSearch = () => {
    const dispatch = useDispatch();
    const v2 = useDesignV2(); // DESIGN V2 — hero header + facts strip
    const label = useSelector((state) => state.djpal.labelResults);
    const name = label?.name || '';
    const { openRowCtx, rowCtxFor } = useRowContextMenu();
    const { playMix, loadingMixId, playingMixId } = useMixPlayback();
    useNativeMixCoverMode(); // re-render when the native cover flag flips
    const [tracksQuery, setTracksQuery] = useState('');
    const [artistsQuery, setArtistsQuery] = useState('');
    const [onLabelQuery, setOnLabelQuery] = useState('');
    const [mixesQuery, setMixesQuery] = useState('');
    // Which artist-list modal is open: 'supporting' | 'onLabel' | null.
    const [artistsModalKind, setArtistsModalKind] = useState(null);

    const handleSearchTrack = useCallback((e, track) => {
        if (e && e.stopPropagation) e.stopPropagation();
        const query = track.title && track.artist ? `${track.artist} - ${track.title}` : (track.title || '');
        dispatch(setTrackQuery(query));
        dispatch(setActiveSearch('track'));
        dispatch(searchTrack({ trackName: query, trackId: entityId(track), variant: 'A', artworkUrl: track.artwork || track.artworkUrl || track.image || null }));
    }, [dispatch]);

    const handleArtistClick = useCallback((artistName, artistId = null, artworkUrl = null) => {
        if (!artistName) return;
        trackEvent('artist_opened', { artist_id: artistId || undefined, artist_name: artistName, from: 'labelPage' });
        dispatch(setArtistQuery(artistName));
        dispatch(searchArtist({ artistName, artistId: artistId || null, artworkUrl: artworkUrl || null }));
    }, [dispatch]);

    // Same id-only navigation as the artist page's mix rows.
    const handleMixClick = useCallback((mix) => {
        const djLabel = mix.djName || mix.artist || '';
        const title = mix.title || '';
        const displayQuery = djLabel && title ? `${djLabel} - ${title}` : title;
        const filter = mix.id ? { id: mix.id }
            : mix.publicId ? { public_id: mix.publicId }
            : mix.link ? { link: mix.link }
            : null;
        if (!filter) return;
        trackEvent('mix_opened', { mix_id: entityId(mix) || mix.id || undefined, artist: djLabel || undefined, from: 'labelPage' });
        dispatch(setMixQuery(displayQuery));
        dispatch(setActiveSearch('mix'));
        dispatch(searchMixes({ filters: filter }));
    }, [dispatch]);

    const handleMixPlay = useCallback((mix, playContext = null) => {
        if (_isWeb) {
            notifyWebOnly('mix');
            return;
        }
        playMix(mix, playContext);
    }, [playMix]);

    const openLabel = useCallback((l) => {
        const publicId = l?.publicId || l?.id;
        if (!publicId) return;
        dispatch(openMixpediaLabel({ publicId, name: l.name, from: 'labelPage' }));
    }, [dispatch]);

    const tracks = useMemo(() => label?.tracks || [], [label]);
    const supportingArtists = useMemo(() => (Array.isArray(label?.supportingArtists) ? label.supportingArtists : []), [label]);
    const artistsOnLabel = useMemo(() => (Array.isArray(label?.artistsOnLabel) ? label.artistsOnLabel : []), [label]);
    const mixes = useMemo(() => (Array.isArray(label?.mixes) ? label.mixes : []), [label]);
    const stats = label?.stats && typeof label.stats === 'object' ? label.stats : null;

    const q = tracksQuery.trim().toLowerCase();
    const shownTracks = useMemo(() => (q
        ? tracks.filter((t) => `${t.title || ''} ${t.artist || ''}`.toLowerCase().includes(q))
        : tracks), [tracks, q]);
    const aq = artistsQuery.trim().toLowerCase();
    const shownArtists = useMemo(() => (aq
        ? supportingArtists.filter((a) => (a.name || '').toLowerCase().includes(aq))
        : supportingArtists), [supportingArtists, aq]);
    const oq = onLabelQuery.trim().toLowerCase();
    const shownOnLabel = useMemo(() => (oq
        ? artistsOnLabel.filter((a) => (a.name || '').toLowerCase().includes(oq))
        : artistsOnLabel), [artistsOnLabel, oq]);
    const mq = mixesQuery.trim().toLowerCase();
    const shownMixes = useMemo(() => (mq
        ? mixes.filter((m) => `${m.title || ''} ${m.djName || m.artist || ''}`.toLowerCase().includes(mq))
        : mixes), [mixes, mq]);

    // follow + bell state for this label (the header's Follow pill reads followedLabels)
    const labelPublicId = label?.publicId || null;
    useEffect(() => { if (labelPublicId) dispatch(checkFollowedLabels([labelPublicId])); }, [dispatch, labelPublicId]);

    // DESIGN V2 tabs. Lists are keyed `${list}:${sort}` and dropped when the label changes.
    const { canAccess } = useTierGate('full-mixpedia');
    const [v2Tab, setV2TabState] = useState('overview');
    const [artistsSeg, setArtistsSegState] = useState('onLabel');
    const [artistSort, setArtistSort] = useState('plays');
    const [mixSort, setMixSort] = useState('newest');
    const [trackSort, setTrackSort] = useState('played');
    const [tabQuery, setTabQuery] = useState('');
    const [lists, setLists] = useState({});
    // Artists tab: the row opened in place (`${kind}:${artist id}`), one at a time.
    const [openArtist, setOpenArtist] = useState(null);
    const listsRef = useRef(lists); listsRef.current = lists;
    const labelIdRef = useRef(labelPublicId); labelIdRef.current = labelPublicId;
    useEffect(() => { setLists({}); setV2TabState('overview'); setTabQuery(''); setOpenArtist(null); }, [labelPublicId]);
    const fetchLabelArtistTracks = useCallback(async (artist, kind, page = 1) => {
        const token = await dispatch((d, getState) => ensureToken(d, getState));
        const r = await window.electron.djpal.getLabelArtistTracks(token, labelIdRef.current, entityId(artist), { kind, page, limit: 20 });
        if (r?.error || !r?.data) throw new Error(r?.error || 'Could not load the tracks');
        return { tracks: r.data.tracks || [], total: r.data.total ?? null };
    }, [dispatch]);
    const setV2Tab = useCallback((tab, via = 'tab') => {
        setV2TabState((prev) => { if (prev !== tab) trackEvent('label_page_tab_changed', { tab, via }); return tab; });
        setTabQuery('');
    }, []);
    const setArtistsSeg = useCallback((seg) => {
        setArtistsSegState((prev) => { if (prev !== seg) trackEvent('entity_list_segment_changed', { page: 'label', list: 'artists', segment: seg }); return seg; });
    }, []);
    const sortSetter = (list, set) => (v) => { trackEvent('entity_list_sort_changed', { page: 'label', list, sort: v }); set(v); };

    const v2List = v2Tab === 'artists' ? (artistsSeg === 'onLabel' ? 'artists' : 'supporting') : v2Tab;
    const v2Sort = v2Tab === 'artists' ? artistSort : v2Tab === 'mixes' ? mixSort : trackSort;
    const loadList = useCallback(async (list, sort, more = false) => {
        const publicId = labelIdRef.current;
        if (!publicId) return;
        const key = `${list}:${sort}`;
        const cur = listsRef.current[key];
        if (cur?.loading || (more ? !cur?.hasMore : (cur && !cur.error))) return;
        const page = more ? cur.page + 1 : 1;
        setLists((prev) => ({ ...prev, [key]: { items: [], ...prev[key], loading: true, error: null } }));
        let result;
        try {
            const token = await dispatch((d, getState) => ensureToken(d, getState));
            result = await window.electron.djpal.getLabelList(token, publicId, list, { page, limit: LIST_PAGE_SIZE, sort });
        } catch (e) {
            result = { error: e?.message || 'Could not load the list' };
        }
        if (labelIdRef.current !== publicId) return;
        setLists((prev) => {
            const was = prev[key] || { items: [] };
            if (result?.error || !result?.data) return { ...prev, [key]: { ...was, loading: false, error: result?.error || 'Could not load the list' } };
            const d = result.data;
            return { ...prev, [key]: { items: more ? [...was.items, ...(d.items || [])] : (d.items || []), total: d.total, hasMore: !!d.hasMore, page, loading: false, error: null } };
        });
    }, [dispatch]);
    useEffect(() => {
        if (!v2 || v2Tab === 'overview' || !labelPublicId) return;
        loadList(v2List, v2Sort);
    }, [v2, v2Tab, v2List, v2Sort, labelPublicId, loadList]);

    if (!label) return null;

    const firstChar = (name || '?').charAt(0).toUpperCase();
    const total = label.totalTracks ?? label.trackCount ?? tracks.length;
    const hasMore = !label.pending && !label.error && tracks.length < total;
    const loaded = !label.pending && !label.error;

    // Pending: the artist page's loading skeleton one for one — header
    // (avatar + name), six stat tiles, then the four card placeholders.
    if (label.pending && !label.error) {
        return (
            <div className="djpal-artist-search djpal-label-search">
                <div className="djpal-results djpal-artist-loading">
                    <div className="djpal-artist-header djpal-label-header">
                        {safeArtwork(label.artworkUrl) ? (
                            <CoverArt className="artist-avatar-lg label-avatar-lg" src={label.artworkUrl} size={96} eager alt={name} />
                        ) : (
                            <div className="artist-avatar-lg label-avatar-lg avatar-pending" />
                        )}
                        <div className="artist-info">
                            <div className="artist-name-lg">{name}</div>
                        </div>
                    </div>
                    <div className="djpal-skel">
                        <div className="djpal-stats-grid">
                            {[0, 1, 2, 3, 4, 5].map((i) => (
                                <div className="stat-item" key={i}>
                                    <div className="djpal-skel-block skel-stat-value" />
                                    <div className="djpal-skel-block skel-stat-label" />
                                </div>
                            ))}
                        </div>
                        <div className="djpal-two-col">
                            <CardSkeleton />
                            <CardSkeleton />
                        </div>
                        <div className="djpal-two-col">
                            <CardSkeleton />
                            <CardSkeleton />
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    const tiles = stats ? [
        ['tracksReleased', 'Tracks released'],
        ['tracksSupported', 'Tracks with DJ support'],
        ['mixesWithLabelTracks', 'Mixes with label tracks'],
        ['djPlays', 'Total DJ plays'],
        ['supportingDjs', 'Supporting DJs'],
        ['artistsOnLabel', 'Artists on label'],
    ].filter(([k]) => typeof stats[k] === 'number') : [];

    const supportersTotal = typeof stats?.supportingDjs === 'number' ? stats.supportingDjs : supportingArtists.length;
    const hasSupportedLine = supportingArtists.length > 0;
    const linksRow = <ArtistLinksRow externalIds={label.externalIds} artistName={name} event="label_link_opened" />;

    // DESIGN V2 — hero: round logo, "Label" kicker, country / family as
    // meta, the supported-by line as sub, Play (first label track) + links.
    const v2PlayId = tracks.length > 0 ? `label-${entityId(tracks[0]) || 0}` : null;
    const renderV2Hero = () => (
        <HeroHeader
            className="v2-label-hero"
            round
            art={safeArtwork(label.artworkUrl) ? label.artworkUrl : <div className="v2-hero-letter">{firstChar}</div>}
            artAlt={name}
            ambientKey={label.publicId || name}
            kicker="Label"
            title={name}
            meta={[
                label.country || null,
                label.parent?.name ? <>Part of <span className="supported-others" onClick={() => openLabel(label.parent)}>{label.parent.name}</span></> : null,
                label.subLabels?.length > 0 ? (
                    <>Sub-labels {label.subLabels.map((sl, i) => (
                        <React.Fragment key={sl.publicId || sl.id || `sub-${i}`}>{i > 0 && ', '}<span className="supported-others" onClick={() => openLabel(sl)}>{sl.name}</span></React.Fragment>
                    ))}</>
                ) : null,
                typeof total === 'number' && total > 0 ? <><b>{fmt(total)}</b> tracks</> : null,
            ]}
            subIcon={hasSupportedLine ? <SupportedCheck /> : null}
            sub={hasSupportedLine ? (
                <span>
                    <span className="supported-grey">Supported by </span>
                    {supportingArtists.slice(0, 2).map((a, i) => (
                        <React.Fragment key={entityId(a) || a.name}>
                            {i > 0 && <span className="supported-grey">, </span>}
                            <span className="supported-name" onClick={() => handleArtistClick(a.name, entityId(a), a.artworkUrl)}>{a.name}</span>
                        </React.Fragment>
                    ))}
                    {supportersTotal > 2 && (
                        <>
                            <span className="supported-grey"> and </span>
                            <span className="supported-others" onClick={() => setArtistsModalKind('supporting')}>{fmt(supportersTotal - 2)} others</span>
                        </>
                    )}
                </span>
            ) : null}
            facts={loaded ? tiles.map(([k, title]) => ({ value: fmt(stats[k]), label: title.replace('Total DJ plays', 'DJ plays') })) : []}
            actions={(
                <>
                    <HeroPlayButton previewId={v2PlayId} disabled={!v2PlayId} title={v2PlayId ? 'Play the top label track' : 'Nothing to play yet'} />
                    <span className="like-bell-group v2-follow-group">
                        <FollowLabelButton prominent label={{ publicId: label.publicId, name, artworkUrl: label.artworkUrl }} from="label_header" />
                        <NotifyBellButton entityType="label" entity={{ publicId: label.publicId, name, artworkUrl: label.artworkUrl }} from="label_header" />
                    </span>
                    <div className="v2-hero-links">{linksRow}</div>
                </>
            )}
        />
    );

    const renderHeader = () => (
        <div className="djpal-artist-header djpal-label-header">
            {safeArtwork(label.artworkUrl) ? (
                <CoverArt className="artist-avatar-lg label-avatar-lg" src={label.artworkUrl} size={96} eager alt={name} />
            ) : (
                <div className="artist-avatar-lg label-avatar-lg">{firstChar}</div>
            )}
            <div className="artist-info">
                <div className="artist-name-lg">{name}</div>
                <span className="like-bell-group label-follow-group">
                    <FollowLabelButton prominent label={{ publicId: label.publicId, name, artworkUrl: label.artworkUrl }} from="label_header" />
                    <NotifyBellButton entityType="label" entity={{ publicId: label.publicId, name, artworkUrl: label.artworkUrl }} from="label_header" />
                </span>
                {/* Byline = the country alone. The socials icon row lives on
                    the "Supported by" line (like the artist page) and only
                    falls back to the byline's right end when there are no
                    supporters to build that line from. */}
                {(label.country || !hasSupportedLine) && (
                    <div className="artist-byline label-byline">
                        {label.country && <span className="label-byline-text label-country">{label.country}</span>}
                        {!hasSupportedLine && linksRow}
                    </div>
                )}
                {(label.parent?.name || label.subLabels?.length > 0) && (
                    <div className="artist-genre label-family">
                        {label.parent?.name && (
                            <span className="label-family-group">
                                <span className="supported-grey">Part of </span>
                                <span className="djpal-label-chip clickable" onClick={() => openLabel(label.parent)} role="button" tabIndex={0}>{label.parent.name}</span>
                            </span>
                        )}
                        {label.subLabels?.length > 0 && (
                            <span className="label-family-group">
                                <span className="supported-grey">Sub-labels </span>
                                {label.subLabels.map((sl, i) => (
                                    <span className="djpal-label-chip clickable" key={sl.publicId || sl.id || `sub-${i}`} onClick={() => openLabel(sl)} role="button" tabIndex={0}>{sl.name}</span>
                                ))}
                            </span>
                        )}
                    </div>
                )}
            </div>
        </div>
    );

    // "Supported by A, B and N others" — the artist page's line verbatim,
    // with the socials icon row at its right end.
    const renderSupportedLine = () => hasSupportedLine && (
        <div className="artist-supported-line artist-supported-line--below">
            <SupportedCheck />
            <span>
                <span className="supported-grey">Supported by </span>
                {supportingArtists.slice(0, 2).map((a, i) => (
                    <React.Fragment key={entityId(a) || a.name}>
                        {i > 0 && <span className="supported-grey">, </span>}
                        <span className="supported-name" onClick={() => handleArtistClick(a.name, entityId(a), a.artworkUrl)}>{a.name}</span>
                    </React.Fragment>
                ))}
                {supportersTotal > 2 && (
                    <>
                        <span className="supported-grey"> and </span>
                        <span className="supported-others" onClick={() => setArtistsModalKind('supporting')}>{fmt(supportersTotal - 2)} others</span>
                    </>
                )}
            </span>
            {linksRow}
        </div>
    );

    const renderTrackRow = (track, i, list) => {
        const art = track.artwork || track.artworkUrl || track.image;
        const rowKey = `label-${entityId(track) || i}`;
        return (
            <div className="djpal-track-row clickable" key={rowKey} onContextMenu={openRowCtx(rowKey)} {...delayedClick(() => handleSearchTrack(null, track))}>
                <DJPalAudioPreview src={track.previewUrl || track.sampleUrl || track.preview_url || track.preview} youtubeId={track.youtubeId} soundcloudUrl={track.soundcloudUrl} trackId={track.id} durationSec={track.durationSec ?? track.duration} pageUrl={track.pageUrl} previewId={rowKey} trackName={track.title} trackArtist={track.artist} trackCover={art} playContext={{ items: list, index: i, type: 'tracks' }} playbackBlocked={track.playbackBlocked}>
                    <div className="track-artwork">
                        {art ? <CoverArt src={art} size={48} /> : <div className="artwork-placeholder">&#9835;</div>}
                    </div>
                </DJPalAudioPreview>
                <div className="track-info">
                    <span className="track-label" title={track.artist ? `${track.artist} – ${track.title}` : track.title}>{track.title}</span>
                    {(track.artist || (track.artists && track.artists.length > 0)) && (
                        <span className="track-sublabel track-artist-line">
                            <MultiArtistText
                                artists={track.artists}
                                fallbackText={track.artist}
                                segmentClassName="clickable-artist"
                                onSelect={(a) => handleArtistClick(a.name, entityId(a), a.artworkUrl)}
                            />
                        </span>
                    )}
                </div>
                <LikeButton track={track} />
                <TrackContextMenu
                    track={track}
                    onSearch={handleSearchTrack}
                    desktopLike
                    openAtPoint={rowCtxFor(rowKey)}
                />
            </div>
        );
    };

    // The artist page's mix row: cover with play overlay, title, DJ · date,
    // like + context menu; click opens the mix page.
    const renderMixRow = (mix, i, list) => {
        const mixArt = resolveMixCover(mix);
        const mixLabel = (mix.djName || mix.artist) ? `${mix.djName || mix.artist} - ${mix.title}` : mix.title;
        const isLoading = loadingMixId === mix.id;
        const isPlaying = playingMixId === mix.id;
        const canPlay = canPlayMix(mix);
        const rowKey = `label-mix-${entityId(mix) || mix.id || i}`;
        return (
            <div
                className="djpal-track-row clickable"
                key={rowKey}
                onContextMenu={openRowCtx(rowKey)}
                {...delayedClick(() => handleMixClick(mix))}
            >
                <div
                    className={`track-artwork${canPlay ? ' clickable' : ''}`}
                    onClick={canPlay ? (e) => { e.stopPropagation(); handleMixPlay(mix, { items: list, index: i }); } : undefined}
                >
                    {canPlay && (
                        <div className={`mix-play-overlay${isLoading ? ' loading' : ''}${isPlaying ? ' playing' : ''}`}>
                            {isLoading ? <span className="mix-play-spinner" /> : <span className={`play-icon${isPlaying ? ' paused' : ''}`}>{isPlaying ? <PauseIcon /> : <PlayIcon />}</span>}
                        </div>
                    )}
                    {mixArt ? <CoverArt src={mixArt} size={48} /> : <div className="artwork-placeholder">&#9835;</div>}
                </div>
                <div className="track-info">
                    <span className="track-label" title={mixLabel}>{mix.title || mixLabel}</span>
                    {((mix.artists?.length > 0 || mix.djName || mix.artist) || mix.date) && (
                        <span className="track-sublabel track-artist-line">
                            <MultiArtistText
                                artists={mix.artists}
                                fallbackText={mix.djName || mix.artist}
                                segmentClassName="clickable-artist"
                                onSelect={(a) => handleArtistClick(a.name, entityId(a), a.artworkUrl)}
                            />
                            {(mix.artists?.length > 0 || mix.djName || mix.artist) && mix.date ? ' · ' : ''}
                            {mix.date}
                            {v2 && mix.labelTracksCount > 0 && <span className="v2-faint"> · {mix.labelTracksCount} label {mix.labelTracksCount === 1 ? 'track' : 'tracks'}</span>}
                        </span>
                    )}
                </div>
                <MixLikeButton mix={mix} />
                <MixContextMenu mix={mix} openAtPoint={rowCtxFor(rowKey)} />
            </div>
        );
    };

    const renderTracksCard = () => (
        <div className="djpal-card djpal-label-tracks">
            <div className="djpal-card-header">
                <h3>Label tracks played by DJs</h3>
                {loaded && tracks.length > 0 && (
                    <div className="djpal-card-header-actions">
                        <CardSearch
                            value={tracksQuery}
                            onChange={setTracksQuery}
                            placeholder="Filter tracks…"
                            ariaLabel="Filter label tracks"
                        />
                    </div>
                )}
            </div>
            {label.error ? null : tracks.length === 0 ? (
                <div className="djpal-empty-hint">No tracks on this label yet</div>
            ) : (
                <DJPalScrollableList
                    maxHeight="420px"
                    onScrollBottom={hasMore && !q && !label.loadingMore ? () => dispatch(loadMoreLabelTracks()) : undefined}
                >
                    {shownTracks.length === 0 && q && <div className="djpal-empty-hint">No matches found.</div>}
                    {shownTracks.map((t, i) => renderTrackRow(t, i, shownTracks))}
                    {label.loadingMore && <div className="djpal-load-more-spinner"><span className="mix-play-spinner" /></div>}
                    {/* Inside the list: .djpal-two-col gives the card's last
                        child flex:1, which must stay the list itself. */}
                    {hasMore && !label.loadingMore && !q && (
                        <div className="djpal-label-load-more">
                            <button type="button" className="djpal-label-load-more-btn" onClick={() => dispatch(loadMoreLabelTracks())}>
                                Load more ({(total - tracks.length).toLocaleString()} remaining)
                            </button>
                        </div>
                    )}
                </DJPalScrollableList>
            )}
        </div>
    );

    const renderArtistBubble = (a, i, keyPrefix) => (
        <div className="djpal-artist-bubble clickable" key={entityId(a) || `${keyPrefix}-${i}`} onClick={() => handleArtistClick(a.name, entityId(a), a.artworkUrl)}>
            {safeArtwork(a.artworkUrl) ? (
                <CoverArt className="artist-avatar" src={a.artworkUrl} size={24} alt={a.name} />
            ) : (
                <div className="artist-avatar">{(a.name || '?').charAt(0).toUpperCase()}</div>
            )}
            <span>{a.name}</span>
        </div>
    );

    // Artist-bubble card (filter / list modal / full page in the header).
    const renderBubbleCard = ({ className, title, list, shown, query, setQuery, filterAria, modalKind, emptyHint, keyPrefix }) => (
        <div className={`djpal-card ${className}`}>
            <div className="djpal-card-header">
                <h3>{title}</h3>
                {list.length > 0 && (
                    <div className="djpal-card-header-actions">
                        <CardSearch
                            value={query}
                            onChange={setQuery}
                            placeholder="Filter artists…"
                            ariaLabel={filterAria}
                        />
                        <button className="djpal-card-expand-btn" title="List view — details & sorting" onClick={() => setArtistsModalKind(modalKind)}>
                            <ListIcon />
                        </button>
                        <button
                            className="djpal-card-expand-btn"
                            title="Browse & follow all"
                            onClick={() => dispatch(openMixpediaArtistsGrid({
                                title,
                                subtitle: name,
                                artists: list.map((a) => ({ name: a.name, id: entityId(a) || null, artworkUrl: a.artworkUrl || null })),
                                from: 'labelPage',
                            }))}
                        >
                            <OpenIcon />
                        </button>
                    </div>
                )}
            </div>
            {list.length === 0 ? (
                <div className="djpal-empty-hint">{emptyHint}</div>
            ) : (
                <DJPalScrollableList maxHeight="420px">
                    {shown.length === 0 && query.trim() && <div className="djpal-empty-hint">No matches found.</div>}
                    {shown.map((a, i) => renderArtistBubble(a, i, keyPrefix))}
                </DJPalScrollableList>
            )}
        </div>
    );

    const renderSupportersCard = () => renderBubbleCard({
        className: 'djpal-label-supporters',
        title: 'Artists supporting the label',
        list: supportingArtists,
        shown: shownArtists,
        query: artistsQuery,
        setQuery: setArtistsQuery,
        filterAria: 'Filter artists supporting the label',
        modalKind: 'supporting',
        emptyHint: 'No DJ support data available yet',
        keyPrefix: 'supporting',
    });

    const renderArtistsOnLabelCard = () => renderBubbleCard({
        className: 'djpal-label-roster',
        title: 'Artists on label',
        list: artistsOnLabel,
        shown: shownOnLabel,
        query: onLabelQuery,
        setQuery: setOnLabelQuery,
        filterAria: 'Filter artists on label',
        modalKind: 'onLabel',
        emptyHint: 'No artists on this label yet',
        keyPrefix: 'on-label',
    });

    // No list modal / full page here: MixesListModal and the "Show all"
    // mix search are bound to an artist (section + artistName), and the
    // mix search has no label filter yet.
    const renderMixesCard = () => (
        <div className="djpal-card djpal-label-mixes">
            <div className="djpal-card-header">
                <h3>Mixes with label tracks</h3>
                {mixes.length > 0 && (
                    <div className="djpal-card-header-actions">
                        <CardSearch
                            value={mixesQuery}
                            onChange={setMixesQuery}
                            placeholder="Filter mixes…"
                            ariaLabel="Filter mixes with label tracks"
                        />
                    </div>
                )}
            </div>
            {mixes.length === 0 ? (
                <div className="djpal-empty-hint">No mixes with label tracks found</div>
            ) : (
                <DJPalScrollableList maxHeight="420px">
                    {shownMixes.length === 0 && mq && <div className="djpal-empty-hint">No matches found.</div>}
                    {shownMixes.map((m, i) => renderMixRow(m, i, shownMixes))}
                </DJPalScrollableList>
            )}
        </div>
    );

    const hasSupportersCard = Array.isArray(label.supportingArtists);
    const hasRosterCard = Array.isArray(label.artistsOnLabel);
    const hasMixesCard = Array.isArray(label.mixes);

    const modalArtists = artistsModalKind === 'onLabel' ? artistsOnLabel : supportingArtists;

    // ───────── DESIGN V2 tabs ─────────
    const tracksTotal = typeof stats?.tracksSupported === 'number' ? stats.tracksSupported : total;
    const mixesTotal = label.totalMixes ?? stats?.mixesWithLabelTracks ?? mixes.length;
    const rosterTotal = typeof stats?.artistsOnLabel === 'number' ? stats.artistsOnLabel : artistsOnLabel.length;
    const artistMeta = (a, kind) => {
        const n = a.tracksCount;
        if (!n) return null;
        return kind === 'onLabel' ? `${fmtNum(n)} ${n === 1 ? 'track' : 'tracks'} on the label` : `played ${fmtNum(n)} label ${n === 1 ? 'track' : 'tracks'}`;
    };
    // ranked = the Artists tab: the row opens in place to which label tracks (and
    // where / when); the name stays the link to the artist page.
    const renderV2ArtistRow = (a, i, kind, ranked) => {
        const openKey = `${kind}:${entityId(a) || a.name}`;
        const isOpen = ranked && openArtist === openKey;
        const canOpen = ranked && !!entityId(a);
        return (
        <React.Fragment key={entityId(a) || `${kind}-${i}`}>
        <div className={`djpal-artist-bubble clickable${isOpen ? ' is-open' : ''}`} onClick={() => {
            if (!canOpen) { handleArtistClick(a.name, entityId(a), a.artworkUrl); return; }
            setOpenArtist((prev) => { if (prev !== openKey) trackEvent('network_artist_expanded', { list: kind === 'onLabel' ? 'label_roster' : 'label_supporting' }); return prev === openKey ? null : openKey; });
        }}>
            {ranked && <span className="v2-row-rank">{i + 1}</span>}
            {safeArtwork(a.artworkUrl) ? (
                <CoverArt className="artist-avatar" src={a.artworkUrl} size={24} alt={a.name} fallback={<div className="artist-avatar">{(a.name || '?').charAt(0).toUpperCase()}</div>} />
            ) : (
                <div className="artist-avatar">{(a.name || '?').charAt(0).toUpperCase()}</div>
            )}
            <span>{canOpen ? <span className="v2-row-name-link" onClick={(e) => { e.stopPropagation(); handleArtistClick(a.name, entityId(a), a.artworkUrl); }}>{a.name}</span> : a.name}</span>
            {artistMeta(a, kind) && <span className="v2-row-meta">{artistMeta(a, kind)}</span>}
            {canOpen && <span className="v2-row-chevron" aria-hidden="true"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9" /></svg></span>}
        </div>
        {isOpen && (
            <CoPlayedTracks
                artist={a}
                pageable
                fetchTracksFor={(x, page) => fetchLabelArtistTracks(x, kind, page)}
                countLabel={(t) => (t.playedCount ? (kind === 'onLabel'
                    ? `in ${fmtNum(t.mixesTotal || t.playedCount)} ${(t.mixesTotal || t.playedCount) === 1 ? 'mix' : 'mixes'}`
                    : `played ${fmtNum(t.playedCount)} ${t.playedCount === 1 ? 'time' : 'times'}`) : null)}
                onTrackClick={(t) => handleSearchTrack(null, t)}
                onMixClick={(mx) => handleMixClick({ publicId: mx.publicId, title: mx.eventName || mx.artist || '', link: mx.link })}
            />
        )}
        </React.Fragment>
        );
    };
    const overviewCard = ({ className, title, count, onAll, empty, children }) => (
        <div className={`djpal-card ${className}`}>
            <SectionHeader title={title} link={count > 0 ? `See all ${fmtNum(count)}` : null} onLink={onAll} />
            {empty ? <div className="djpal-empty-hint">{empty}</div> : <div className="v2-overview-rows">{children}</div>}
        </div>
    );
    const renderV2Overview = () => (
        <>
            <div className="djpal-two-col">
                {overviewCard({
                    className: 'djpal-label-tracks', title: 'Label tracks played by DJs', count: tracksTotal,
                    onAll: () => setV2Tab('tracks', 'see_all'), empty: tracks.length === 0 ? 'No tracks on this label yet' : null,
                    children: tracks.slice(0, V2_OVERVIEW_ROWS).map((t, i, l) => renderTrackRow(t, i, l)),
                })}
                {overviewCard({
                    className: 'djpal-label-supporters', title: 'Artists supporting the label', count: supportersTotal,
                    onAll: () => { setArtistsSegState('supporting'); setV2Tab('artists', 'see_all'); },
                    empty: supportingArtists.length === 0 ? 'No DJ support data available yet' : null,
                    children: supportingArtists.slice(0, V2_OVERVIEW_ROWS).map((a, i) => renderV2ArtistRow(a, i, 'supporting', false)),
                })}
            </div>
            <div className="djpal-two-col">
                {overviewCard({
                    className: 'djpal-label-roster', title: 'Artists on the label', count: rosterTotal,
                    onAll: () => { setArtistsSegState('onLabel'); setV2Tab('artists', 'see_all'); },
                    empty: artistsOnLabel.length === 0 ? 'No artists on this label yet' : null,
                    children: artistsOnLabel.slice(0, V2_OVERVIEW_ROWS).map((a, i) => renderV2ArtistRow(a, i, 'onLabel', false)),
                })}
                {overviewCard({
                    className: 'djpal-label-mixes', title: 'Mixes with label tracks', count: mixesTotal,
                    onAll: () => setV2Tab('mixes', 'see_all'), empty: mixes.length === 0 ? 'No mixes with label tracks found' : null,
                    children: mixes.slice(0, V2_OVERVIEW_ROWS).map((m, i, l) => renderMixRow(m, i, l)),
                })}
            </div>
        </>
    );
    const renderV2ListTab = () => {
        const st = lists[`${v2List}:${v2Sort}`];
        const items = st?.items || [];
        const tq = tabQuery.trim().toLowerCase();
        const text = (x) => (v2Tab === 'artists' ? (x.name || '')
            : v2Tab === 'mixes' ? `${x.title || ''} ${x.djName || x.artist || ''}`
            : `${x.title || ''} ${x.artist || ''}`).toLowerCase();
        const filtered = tq ? items.filter((x) => text(x).includes(tq)) : items;
        const shown = canAccess ? filtered : filtered.slice(0, FREE_RESULTS_LIMIT);
        const listTotal = st?.total ?? (v2Tab === 'mixes' ? mixesTotal : v2Tab === 'tracks' ? tracksTotal : artistsSeg === 'onLabel' ? rosterTotal : supportersTotal);
        const segments = v2Tab === 'artists' ? [
            { key: 'onLabel', label: 'On the label', count: rosterTotal },
            { key: 'supporting', label: 'Supporting it', count: supportersTotal },
        ] : [{ key: v2Tab, label: v2Tab === 'mixes' ? 'Mixes with label tracks' : 'Label tracks played by DJs', count: v2Tab === 'mixes' ? mixesTotal : tracksTotal }];
        const sortProps = v2Tab === 'artists' ? { value: artistSort, onChange: sortSetter('artists', setArtistSort), modes: ARTIST_SORTS, labels: ARTIST_SORT_LABELS }
            : v2Tab === 'mixes' ? { value: mixSort, onChange: sortSetter('mixes', setMixSort), modes: MIX_SORTS, labels: MIX_SORT_LABELS }
            : { value: trackSort, onChange: sortSetter('tracks', setTrackSort), modes: TRACK_SORTS, labels: TRACK_SORT_LABELS };
        const noun = v2Tab === 'artists' ? 'artists' : v2Tab;
        return (
            <>
                <ListToolbar segments={segments} value={v2Tab === 'artists' ? artistsSeg : v2Tab} onChange={v2Tab === 'artists' ? setArtistsSeg : () => {}}>
                    <SortChips {...sortProps} />
                    <CardSearch value={tabQuery} onChange={setTabQuery} placeholder={`Filter ${noun}…`} ariaLabel={`Filter the label's ${noun}`} />
                    {v2Tab === 'tracks' && shown.length > 0 && <PlayAllButton tracks={shown} from="label_tracks" />}
                </ListToolbar>
                <div className="djpal-two-col v2-single">
                    <div className={`djpal-card djpal-label-${v2Tab}-list`}>
                        {st?.loading && items.length === 0 && <CardSkeleton />}
                        {st?.error && items.length === 0 && (
                            <div className="djpal-empty-hint">
                                Couldn't load this list. <button type="button" className="v2-btn sm quiet" onClick={() => loadList(v2List, v2Sort)}>Try again</button>
                            </div>
                        )}
                        {st && !st.loading && !st.error && items.length === 0 && <div className="djpal-empty-hint">Nothing here yet</div>}
                        {tq && items.length > 0 && filtered.length === 0 && <div className="djpal-empty-hint">No matches found.</div>}
                        {shown.map((x, i) => (v2Tab === 'artists' ? renderV2ArtistRow(x, i, artistsSeg, true)
                            : v2Tab === 'mixes' ? renderMixRow(x, i, shown)
                            : renderTrackRow(x, i, shown)))}
                        {canAccess && items.length > 0 && (
                            <AutoLoad onMore={() => loadList(v2List, v2Sort, true)} loading={!!st?.loading} hasMore={!!st?.hasMore && !tq} />
                        )}
                        {!canAccess && !tq && listTotal > FREE_RESULTS_LIMIT && (
                            <button className="djpal-tier-lock-msg" onClick={() => dispatch(showUpgradeModal({ feature: 'full-mixpedia', context: { total: listTotal } }))}>See all {fmtNum(listTotal)} results with <GoatBadge /></button>
                        )}
                    </div>
                </div>
            </>
        );
    };

    return (
        <div className={`djpal-artist-search djpal-label-search${v2 ? ' v2-ambient v2-entity-page' : ''}`}>
            {label.error && (
                <ErrorBanner
                    error={label.error}
                    surface="mixpedia_search"
                    onRetry={() => dispatch(openMixpediaLabel({ publicId: label.publicId, name, from: 'retry' }))}
                />
            )}
            <div className="djpal-results">
                {v2 ? (<>
                    {renderV2Hero()}
                    {!label.error && <div className="v2-tabs-row"><PageTabs className="v2-entity-tabs" items={V2_TABS} value={v2Tab} onChange={setV2Tab} /></div>}
                    {!label.error && (v2Tab === 'overview' ? renderV2Overview() : renderV2ListTab())}
                </>) : (<>
                {renderHeader()}
                {renderSupportedLine()}
                </>)}

                {!v2 && loaded && tiles.length > 0 && (
                    <div className="djpal-stats-grid">
                        {tiles.map(([k, title]) => (
                            <div className="stat-item" key={k}>
                                <div className="stat-value">{fmt(stats[k])}</div>
                                <div className="stat-label">{title}</div>
                            </div>
                        ))}
                    </div>
                )}

                {/* Two rows of two cards like the artist page; an older
                    server missing an array gets the surviving card alone
                    at full width (or no second row at all). */}
                {label.error || v2 ? null : (
                    <>
                        {hasSupportersCard ? (
                            <div className="djpal-two-col">
                                {renderTracksCard()}
                                {renderSupportersCard()}
                            </div>
                        ) : renderTracksCard()}
                        {hasRosterCard && hasMixesCard ? (
                            <div className="djpal-two-col">
                                {renderArtistsOnLabelCard()}
                                {renderMixesCard()}
                            </div>
                        ) : hasRosterCard ? renderArtistsOnLabelCard()
                            : hasMixesCard ? renderMixesCard() : null}
                    </>
                )}
            </div>

            {artistsModalKind && (
                <ArtistsListModal
                    title={artistsModalKind === 'onLabel' ? 'Artists on label' : 'Artists supporting the label'}
                    subtitle={name || null}
                    artists={modalArtists}
                    getSubtitle={(a) => {
                        const n = a.tracksCount;
                        if (!n) return null;
                        return artistsModalKind === 'onLabel'
                            ? `${n} ${n === 1 ? 'track' : 'tracks'} on the label`
                            : `played ${n} ${n === 1 ? 'track' : 'tracks'}`;
                    }}
                    onArtistClick={(a) => handleArtistClick(a.name, entityId(a), a.artworkUrl)}
                    onClose={() => setArtistsModalKind(null)}
                />
            )}
        </div>
    );
};

export default LabelSearch;
